import { addDays } from "date-fns";
import { useCallback, useEffect, useRef, useState } from "react";

import { PaymentsSubscription } from "~/api/Api";
import { UnstyledButton } from "~/components/mantine/button/Button";
import PendingStatus from "~/components/ui/pendingStatus/PendingStatus";
import { useTeam } from "~/contexts/TeamProvider";

import { useCardDialog } from "../cardDialog/useCardDialog";
import { TRIAL_FALLBACK_DAYS } from "../consts";
import { isAutoptoCardRequired, isAutoptoTrial } from "../isAutoptoTrial";
import styles from "./TrialCountdownBar.module.scss";

const SECOND_MS = 1000;

// Pages sized to the viewport subtract the bar through this variable (0px
// without the bar, see index.css).
const TOP_BAR_HEIGHT_VAR = "--top-bar-height";
// When the trial runs out, the status changes only after Stripe's webhook:
// recheck the team for a while so the app moves on (to the billing block).
const EXPIRED_REFETCH_INTERVAL_MS = 15 * SECOND_MS;
const EXPIRED_REFETCH_DURATION_MS = 5 * 60 * SECOND_MS;

const getTrialEnd = (subscription: PaymentsSubscription) =>
  subscription.trial_end_at
    ? new Date(subscription.trial_end_at)
    : addDays(new Date(subscription.created_at), TRIAL_FALLBACK_DAYS);

const splitRemaining = (remainingMs: number) => {
  const totalSeconds = Math.max(0, Math.floor(remainingMs / SECOND_MS));

  return [
    { label: "days", value: String(Math.floor(totalSeconds / 86400)) },
    {
      label: "hrs",
      value: String(Math.floor((totalSeconds % 86400) / 3600)).padStart(2, "0"),
    },
    {
      label: "min",
      value: String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, "0"),
    },
    { label: "sec", value: String(totalSeconds % 60).padStart(2, "0") },
  ];
};

const Countdown = ({ endsAt }: { endsAt: Date }) => {
  const { refetchTeam } = useTeam();
  const [now, setNow] = useState(() => Date.now());

  const isExpired = endsAt.getTime() <= now;

  useEffect(() => {
    const intervalId = window.setInterval(() => setNow(Date.now()), SECOND_MS);

    return () => window.clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (!isExpired) return;

    void refetchTeam();
    const intervalId = window.setInterval(
      () => void refetchTeam(),
      EXPIRED_REFETCH_INTERVAL_MS,
    );
    const timeoutId = window.setTimeout(
      () => window.clearInterval(intervalId),
      EXPIRED_REFETCH_DURATION_MS,
    );

    return () => {
      window.clearInterval(intervalId);
      window.clearTimeout(timeoutId);
    };
  }, [isExpired, refetchTeam]);

  return (
    <div className={styles.units} aria-live="off">
      {splitRemaining(endsAt.getTime() - now).map(({ label, value }) => (
        <span key={label} className={styles.unit}>
          <strong className={styles.unitValue}>{value}</strong>
          <span className={styles.unitLabel}>{label}</span>
        </span>
      ))}
    </div>
  );
};

/**
 * Publishes the bar's height while it's on screen: it wraps on narrow
 * screens, and it's gone once the team pays.
 */
const useTopBarHeight = () => {
  const observerRef = useRef<ResizeObserver | null>(null);

  return useCallback((bar: HTMLDivElement | null) => {
    const rootStyle = document.documentElement.style;

    observerRef.current?.disconnect();
    observerRef.current = null;

    if (!bar) {
      rootStyle.removeProperty(TOP_BAR_HEIGHT_VAR);

      return;
    }

    const publish = () =>
      rootStyle.setProperty(TOP_BAR_HEIGHT_VAR, `${bar.offsetHeight}px`);

    publish();

    if (typeof ResizeObserver === "undefined") return;

    observerRef.current = new ResizeObserver(publish);
    observerRef.current.observe(bar);
  }, []);
};

/**
 * Counts the Autopto trial down on every page, with or without a card;
 * gone once the trial ends. Until a card is added it asks for one: a card
 * launches the platform while the trial goes on.
 */
const TrialCountdownBar = () => {
  const { team } = useTeam();
  const { canAddCard, openCardDialog, isOpeningCardDialog, isAwaitingCard } =
    useCardDialog();
  const barRef = useTopBarHeight();

  const subscription = team?.subscription;

  if (!subscription || !isAutoptoTrial(team)) return null;

  return (
    <div ref={barRef} className={styles.bar}>
      <div className={styles.timer}>
        <span className={styles.label}>Your preview ends in</span>
        <Countdown endsAt={getTrialEnd(subscription)} />
      </div>
      {isAutoptoCardRequired(team) &&
        (canAddCard ? (
          isAwaitingCard ? (
            <PendingStatus className={styles.pending}>
              Activating your platform…
            </PendingStatus>
          ) : (
            <UnstyledButton
              className={styles.cta}
              onClick={openCardDialog}
              disabled={isOpeningCardDialog}
            >
              Launch My Platform
            </UnstyledButton>
          )
        ) : (
          <span className={styles.label}>
            Ask your team admin to add a card
          </span>
        ))}
    </div>
  );
};

export default TrialCountdownBar;
